
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { IoEyeSharp, IoDocumentTextOutline } from "react-icons/io5";
import Swal from "sweetalert2";
import { MdRemoveRedEye } from 'react-icons/md';

const EnquiryItemCard = ({ item, globalConfig }) => {
    const navigate = useNavigate();

    const getEnquiryType = (type) => {
        const types = {
            asPerPrescription: "As Per Prescription",
            call: "Call Request",
        };
        return types[type] || "General Enquiry";
    };

    const getStatusBadge = (status) => {
        const styles = {
            awaiting_response: "bg-amber-50 text-amber-600 border-amber-100",
            fulfilled: "bg-emerald-50 text-emerald-600 border-emerald-100",
            in_progress: "bg-blue-50 text-blue-600 border-blue-100",
        };
        return (
            <span className={`px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider border ${styles[status] || "bg-gray-50 text-gray-600"}`}>
                {status?.replace("_", " ")}
            </span>
        );
    };

    const handleOrderSummary = (orderId) => {
        Swal.fire({
            title: "View Order Details?",
            text: "You can see the prescribed products and pricing here.",
            icon: "info",
            showCancelButton: true,
            confirmButtonColor: "#14967F",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, View Summary",
        }).then((result) => {
            if (result.isConfirmed) navigate(`/order-summary/${orderId}`);
        });
    };

    return (
        <div className="w-full bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow duration-300 mb-6">
            <div className="flex flex-col lg:flex-row">

                {/* Image Section - Fixed Aspect Ratio */}
                <div className="w-full h-full flex items-center justify-center p-4 lg:p-1">
                    <div className="relative w-56 h-56 rounded-lg bg-white  overflow-hidden">

                        <div
                            onClick={() => window.open(item?.prescriptionData?.prescriptionImgUrl, '_blank')}
                            className="absolute top-2 right-2 z-10 bg- backdrop-blur-md p-1.5 rounded-full cursor-pointer hover:bg-cyan-50 transition-colors shadow-sm"
                            title="View Prescription"
                        >
                            <MdRemoveRedEye className='text-lg text-[#095D7E]' />
                        </div>

                        {/* Image */}
                        <img
                            src={item?.prescriptionData?.prescriptionImgUrl || "/api/placeholder/200/200"}
                            alt="Prescription"
                            className="w-full h-full object-contain"
                        />
                    </div>
                </div>

                {/* Content Section */}
                <div className="flex-1 p-4 lg:p-3 flex flex-col justify-between">
                    <div>
                        <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-3">
                            <div className="flex flex-col gap-2 w-full">
                                {getStatusBadge(item?.enquiryStatus)}

                                <div className="flex flex-wrap items-center gap-2">
                                    <h3 className="text-sm font-medium text-gray-500 flex items-center gap-1">
                                        <IoDocumentTextOutline className="text-gray-400 shrink-0" />
                                        ID: <span className="text-gray-900 font-mono">{item?._id}</span>
                                    </h3>

                                    {/* Badge moved here - after the ID */}
                                    <div className="px-2 py-0.5 text-[10px] bg-blue-100 text-blue-700 uppercase tracking-wider font-bold rounded border border-blue-200 inline-block">
                                        {/* Use the function here to show the clean name */}
                                        {getEnquiryType(item?.enquiryType)}
                                    </div>

                                </div>
                                <span className="text-xs text-gray-400 px-1">
                                    {moment(item.createdAt).format('DD MMM, YYYY')}
                                </span>
                            </div>

                            {/* Only the Date remains on the right */}
                            <div className="shrink-0 text-right">

                            </div>
                        </div>

                        <hr className="my-3 border-gray-100" />

                        <div className="flex items-center justify-between">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-gray-400 uppercase font-semibold">Total Amount</p>
                                    <p className="text-2xl font-bold text-gray-900 leading-none mt-1">
                                        <span className="text-sm font-medium mr-1 text-gray-500">
                                            {globalConfig?.currencyData?.symbol || "₹"}
                                        </span>
                                        {/* If orderItemData is empty or null, force display to 0 */}
                                        {!item?.orderItemData || item?.orderItemData?.length === 0
                                            ? "0.00"
                                            : (Number(item?.totalAmountToDisplay || 0).toLocaleString('en-IN', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            }))
                                        }
                                    </p>
                                </div>
                            </div>

                            {/* Action Button */}
                            {item.enquiryStatus === "fulfilled" && item?.orderItemData?.length > 0 && (
                                <button
                                    onClick={() => handleOrderSummary(item._id)}
                                    // The 'title' attribute provides the native browser tooltip
                                    title="View Order Summary"
                                    className="flex items-center gap-2 px-2 py-2 bg-emerald-50 text-emerald-600 border-emerald-100 rounded-lg border transition-all active:scale-95 shadow-sm hover:bg-[#14967F] hover:text-white"
                                >
                                    <div className="flex flex-col items-start leading-tight">
                                        <span className="text-[10px] uppercase font-bold opacity-70">
                                            Product Qty
                                        </span>
                                        <span className="text-sm font-bold">
                                            {item?.orderItemData?.length || 0} Items
                                        </span>
                                    </div>
                                    <IoEyeSharp size={20} className="" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnquiryItemCard;